import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Input, List, Select, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import { useWellnessEducation } from '../hooks/useWellnessEducation'

const { Title, Text } = Typography
const { Option } = Select

const WellnessEducation = ({ educationalResources, setEducationalResources }) => {
  const [form] = Form.useForm()
  const [selectedResource, setSelectedResource] = useState(null)
  const { educationResources, isLoading } = useWellnessEducation()

  useEffect(() => {
    form.resetFields()
  }, [selectedResource, form])

  const handleAddResource = (values) => {
    const newResource = {
      id: Date.now(),
      name: values.name,
      type: values.type,
      description: values.description,
      category: values.category,
      format: values.format,
      url: values.url,
      attachments: values.attachments || [],
      assignmentSchedule: values.assignmentSchedule
    }

    setEducationalResources([...educationalResources, newResource])
    form.resetFields()
  }

  const handleDeleteResource = (id) => {
    setEducationalResources(educationalResources.filter((item) => item.id !== id))
    if (selectedResource && selectedResource.id === id) {
      setSelectedResource(null)
    }
  }

  const handleEditResource = () => {
    if (!selectedResource) return

    const values = form.getFieldsValue()
    const updatedResources = educationalResources.map((item) => {
      if (item.id === selectedResource.id) {
        return {
          ...item,
          name: values.name,
          type: values.type,
          description: values.description,
          category: values.category,
          format: values.format,
          url: values.url,
          attachments: values.attachments || [],
          assignmentSchedule: values.assignmentSchedule
        }
      }
      return item
    })

    setEducationalResources(updatedResources)
    setSelectedResource(null)
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Title level={4}>Educational Resources & Materials</Title>
        <Text>Configure educational resources, guides, and learning materials for the wellness plan.</Text>
      </Col>

      <Col span={12} style={{ paddingRight: 12 }}>
        <Card title='Educational Resources' loading={isLoading}>
          <List
            dataSource={educationalResources}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  <Button
                    type='text'
                    danger
                    onClick={() => handleDeleteResource(item.id)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                ]}
              >
                <List.Item.Meta
                  title={item.name}
                  description={`${item.type} - ${item.format}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedResource(item)}
                />
              </List.Item>
            )}
            footer={
              <Button
                type='dashed'
                block
                onClick={() => setSelectedResource(null)}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add New Educational Resource
              </Button>
            }
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card title={selectedResource ? 'Edit Educational Resource' : 'Add Educational Resource'}>
          <Form
            form={form}
            layout='vertical'
            onFinish={selectedResource ? handleEditResource : handleAddResource}
            initialValues={selectedResource || {}}
          >
            <Form.Item name='name' label='Resource Name' rules={[{ required: true, message: 'Please enter a name' }]}>
              <Input placeholder='e.g., Nutrition Guide for Beginners' />
            </Form.Item>

            <Form.Item
              name='type'
              label='Resource Type'
              rules={[{ required: true, message: 'Please select a resource type' }]}
            >
              <Select placeholder='Select resource type'>
                {educationResources.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='description' label='Description'>
              <Input.TextArea rows={3} placeholder='Describe the educational resource...' />
            </Form.Item>

            <Form.Item
              name='category'
              label='Category'
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select placeholder='Select category'>
                <Option value='nutrition'>Nutrition</Option>
                <Option value='physical-activity'>Physical Activity</Option>
                <Option value='stress-management'>Stress Management</Option>
                <Option value='sleep'>Sleep & Recovery</Option>
                <Option value='mental-health'>Mental Health</Option>
                <Option value='general-wellness'>General Wellness</Option>
                <Option value='condition-specific'>Condition Specific</Option>
              </Select>
            </Form.Item>

            <Form.Item name='format' label='Format' rules={[{ required: true, message: 'Please select a format' }]}>
              <Select placeholder='Select format'>
                <Option value='pdf'>PDF Document</Option>
                <Option value='video'>Video</Option>
                <Option value='audio'>Audio</Option>
                <Option value='webinar'>Webinar</Option>
                <Option value='article'>Article</Option>
                <Option value='interactive'>Interactive Content</Option>
                <Option value='infographic'>Infographic</Option>
              </Select>
            </Form.Item>

            <Form.Item name='url' label='Resource URL'>
              <Input placeholder='https://example.com/resource' />
            </Form.Item>

            <Form.Item name='assignmentSchedule' label='Assignment Schedule'>
              <Select placeholder='Select assignment schedule'>
                <Option value='start-of-plan'>Start of Plan</Option>
                <Option value='weekly'>Weekly</Option>
                <Option value='monthly'>Monthly</Option>
                <Option value='quarterly'>Quarterly</Option>
                <Option value='as-needed'>As Needed</Option>
                <Option value='milestone-based'>Milestone Based</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type='primary' htmlType='submit'>
                  {selectedResource ? 'Update' : 'Add'} Educational Resource
                </Button>
                {selectedResource && <Button onClick={() => setSelectedResource(null)}>Cancel</Button>}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </CustomRow>
  )
}

export default WellnessEducation
